/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference types="bingmaps" />

/* eslint-disable no-duplicate-imports */
import {
    Address,
    AddressPurpose,
    CartLine,
    CountryRegionInfo,
    SimpleProduct,
    StateProvinceInfo,
    TenderLine,
    TokenizedPaymentCard
} from '@msdyn365-commerce/retail-proxy';
import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    ArrayExtensions,
    getFallbackImageUrl,
    getSimpleProducts,
    ObjectExtensions,
    ProductInput,
    EmailRegex
} from '@msdyn365-commerce-modules/retail-actions';
import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { action, computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { CheckoutModule, ErrorLocation } from '@msdyn365-commerce/global-state';
import { AutoSuggest } from '@msdyn365-commerce-modules/address';
import { IAutoSuggestOptions } from '@msdyn365-commerce-modules/address';
import { AddressCommon } from '@msdyn365-commerce-modules/address';
import { AddressFormat } from '@msdyn365-commerce-modules/address';
import { AddressItemType } from '@msdyn365-commerce-modules/address';
import { AddressMetaData } from '@msdyn365-commerce-modules/address';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from '@msdyn365-commerce-modules/address';
import { AddressAddUpdate, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { AddressSelect, IAddressSelectProps } from '@msdyn365-commerce-modules/address';
import { AddressShow, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { ICheckoutShippingAddressData } from './checkout-shipping-address.data';
import { ICheckoutShippingAddressProps } from './checkout-shipping-address.props.autogenerated';
import {
    CheckoutPickupCartLines,
    defaultImageSettings,
    ICartLineWithProduct,
    ICheckoutShippingCartLineInfo,
    ICheckoutShippingCartLinesProps
} from './components/checkout-shipping-cartlines-images';
import { ErrorComponent } from '@msdyn365-commerce-modules/address';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/address';

/**
 * The checkout address props interface.
 */
export interface ICheckoutAddressProps extends ICheckoutShippingAddressProps<ICheckoutShippingAddressData>, IModuleStateProps {}

/**
 * The checkout shipping address view state.
 */
export interface ICheckoutShippingAddressViewState {
    isShowAddress: boolean;
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

/**
 * The checkout shipping address state.
 */
export interface ICheckoutAddresState {
    shippingGroups: ICartLineWithProduct[];
    errorMessage?: string;
    deliveryNotes: string;
    companyName: string;
    email: string;
    isCheckboxChecked: boolean;
    streetAddress: string;
    additionalCity: string;
    selectedCountry: string;
    selectedState: string;
    PostalCode: string;
    stateProvinceInfo: StateProvinceInfo[]; // Add this property
    isEditForm: boolean;
    isFirstRender: boolean;
}

export const deliveryNoteKey = 'Additional Information';
export const companyNameKey = 'Company Name';
export const emailKey = 'Email';
export const checkKey = '';
export const streetAddressKey = 'Street Address';
export const CityKey = 'City';
export const selectedCountryKey = 'Country';
export const selectedStateKey = 'State';
export const PostalCodeKey = 'Postal Code';

/**
 * The checkout shipping address view props.
 */
export interface ICheckoutShippingAddressViewProps extends ICheckoutAddressProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    addressListSelectedAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutShippingAddressViewState;
    CheckoutShippingAddress: IModuleProps;
    isUpdating: boolean;
    hasError: boolean;
    showAddress: IAddressShowProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    cartLineImages?: React.ReactNode;
    showAddressSelect: IAddressSelectProps;
    deliveryNotes?: JSX.Element;
    companyName?: JSX.Element;
    email?: JSX.Element;
    isCheckboxChecked?: JSX.Element;
    streetAddress?: JSX.Element;
    additionalCity?: JSX.Element;
    selectedCountry?: JSX.Element;
    selectedState?: JSX.Element;
    PostalCode?: JSX.Element;
    alert?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;
    showAddOrUpdateAddressHandler?(onSaveHandler?: () => void, onCancelHandler?: () => void): IAddressAddUpdateProps;
    showAddressSelectHandler?(
        onAddAddressHandler?: () => void,
        onSaveHandler?: () => void,
        onCancelHandler?: () => void
    ): IAddressSelectProps;
}

/**
 * IExpressPaymentDetail interface.
 */
interface IExpressPaymentDetails {
    email?: string;
    tenderLine?: TenderLine;
    tokenizedPaymentCard?: TokenizedPaymentCard;
    paymentTenderType?: string;
    address?: Address;
    isExpressCheckoutAppliedInCartPage: boolean;
    isExpressAddressAppliedInCartPage?: boolean;
}

/**
 *
 * Address component.
 * @extends {React.Component<ICheckoutShippingAddressProps<ICheckoutShippingAddressData>>}
 */
// @ts-expect-error
@withModuleState
@observer
class CheckoutShippingAddress extends React.Component<ICheckoutAddressProps, ICheckoutAddresState> {
    @observable private currentOperation: AddressOperation;

    @observable private selectedAddress?: Address;

    @observable private addUpdateAddress: Address;

    @observable private countryRegionId: string = 'USA';

    @observable private stateProvinceInfo?: StateProvinceInfo[];

    @observable private customerAddresses: Address[] = [];

    @observable private validationError: object;

    @observable private addressActionResponse?: IAddressResponse;

    @observable private isUpdating?: boolean;

    @observable private hasError?: boolean;

    @observable private addressListSelectedAddress: Address = {};

    private readonly addressCommon: AddressCommon;

    private addressFormat!: AddressFormat;

    private countryRegions: CountryRegionInfo[] = [];

    private addressPurposes: AddressPurpose[] = [];

    private readonly resources: IAddressResource;

    private readonly defaultAddressType: number = 6; // Default to Home

    private readonly telemetryContent?: ITelemetryContent;

    private autoSuggest?: AutoSuggest;

    private deliveryNotesRef: React.RefObject<HTMLTextAreaElement>;

    private companyNameRef: React.RefObject<HTMLInputElement>;

    private emailRef: React.RefObject<HTMLInputElement>;

    private readonly multiplePickupStoreSwitchName: string = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';

    private retailMultiplePickUpOptionEnabled?: boolean = false;

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private hasEmailError: boolean = false;
    private hasStreetError: boolean = false;
    private hasCityError: boolean = false;
    private hasPostalError: boolean = false;
    private hasStateError: boolean = false;
    private emailErrorMessage: string = '';
    private streetErrorMessage: string = '';
    private cityErrorMessage: string = '';
    private postalErrorMessage: string = '';
    private stateErrorMessage: string = '';

    public constructor(props: ICheckoutAddressProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.state = {
            shippingGroups: [],
            deliveryNotes: this._getAttributeValue(deliveryNoteKey) || '',
            companyName: this._getAttributeValue(companyNameKey) || '',
            email: this._getAttributeValue(emailKey) || '',
            isCheckboxChecked: this._getAttributeValue(checkKey) || '',
            streetAddress: this._getAttributeValue(streetAddressKey) || '',
            additionalCity: this._getAttributeValue(CityKey) || '',
            selectedCountry: this._getAttributeValue(selectedCountryKey) || '',
            selectedState: this._getAttributeValue(selectedStateKey) || '',
            PostalCode: this._getAttributeValue(PostalCodeKey) || '',
            stateProvinceInfo: data.countryStates.result || [],
            isEditForm: false,
            isFirstRender: true
        };

        this.addUpdateAddress = {};
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.deliveryNotesRef = React.createRef();
        this.companyNameRef = React.createRef();
        this.emailRef = React.createRef();
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );
        this.validationError = {};
        this.retailMultiplePickUpOptionEnabled = data.featureState.result?.find(
            feature => feature.Name === this.multiplePickupStoreSwitchName
        )?.IsEnabled;
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions },
            resources
        } = this.props;

        // Initializing data props
        this._dataInitialize(this.props);

        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );

        this.props.data.checkout.then(() => {
            this._setDefaultCountryRegionId();
            this._initModuleState();
        });

        reaction(
            () => this.countryRegionId,
            () => {
                this._getStateProvinces();
            }
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        reaction(
            () => this.props.data.checkout.result?.shippingAddressFromExpressCheckout,
            () => {
                if (!this.expressPaymentDetailsFromCartPage && this._canShip()) {
                    let address = this.props.data.checkout.result?.shippingAddressFromExpressCheckout;

                    if (address?.TwoLetterISORegionName) {
                        const threeLetterIsoRegionName = this.getThreeLetterIsoRegionName(address.TwoLetterISORegionName);

                        address = { ...address, ThreeLetterISORegionName: threeLetterIsoRegionName };

                        if (threeLetterIsoRegionName && threeLetterIsoRegionName !== this.countryRegionId) {
                            this._onCountryChange(threeLetterIsoRegionName);
                        }
                    }

                    this._updateCurrentOperation(AddressOperation.Add, address);
                    this._onAddressAddUpdateSubmit();
                }
            }
        );

        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }

            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }

            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(
                telemetry,
                options,
                channel?.BingMapsApiKey,
                channel?.ChannelCountryRegionISOCode,
                channel?.DefaultLanguageId
            );

            // Customer doesn't have any address. Then add view will be loaded directly. Code for the same to handle that
            if (
                this.props.data.storeSelectorStateManager.result &&
                (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
            ) {
                await this.autoSuggest._loadMapAPI(await this.props.data.storeSelectorStateManager);
            }

            reaction(
                () =>
                    this.props.data.storeSelectorStateManager.result?.loadMapApi &&
                    (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );

            reaction(
                () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
                async () => {
                    await this._attachMapAutoSuggest();
                }
            );
        }

        if (this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging) {
            reaction(
                () => this.props.data.checkout.result?.checkoutError,
                checkoutError => {
                    if (
                        checkoutError &&
                        checkoutError.errorLocation === ErrorLocation.CheckoutShippingAddress &&
                        checkoutError.errorMessage
                    ) {
                        this._setErrorMessage(checkoutError.errorMessage);
                    }
                }
            );

            reaction(
                () => this.props.data.checkout.result?.checkoutErrorFocus,
                checkoutErrorFocus => {
                    if (checkoutErrorFocus === CheckoutModule.CheckoutShippingAddress) {
                        focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
                    }
                }
            );
        }

        await this._initPickupGroup();
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    public shouldComponentUpdate(nextProps: ICheckoutAddressProps, nextState: ICheckoutAddresState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        if (!this._canShip()) {
            return null;
        }

        const {
            config,
            context,
            renderView,
            resources,
            moduleState: { hasError }
        } = this.props;
        const { headingImages, itemsText, singleItemText, errorMessageTitle } = resources;
        const { imageSettings } = config;

        // Line images
        const cartlines: ICheckoutShippingCartLineInfo[] = this.state.shippingGroups
            .filter(x => !ObjectExtensions.isNullOrUndefined(x.product))
            .map(line => ({
                lineId: line.cartLine.LineId ?? '',
                imageProps: {
                    requestContext: context.actionContext.requestContext,
                    className: 'ms-checkout-shipping-address__group-images-lines-line-image',
                    altText: line.product?.Name,
                    src: line.product?.PrimaryImageUrl ?? '',
                    fallBackSrc: getFallbackImageUrl(
                        line.product?.ItemId,
                        context.actionContext.requestContext.apiSettings,
                        context.request.app?.config?.OmniChannelMedia
                    ),
                    gridSettings: context.request.gridSettings!,
                    imageSettings: imageSettings ?? defaultImageSettings,
                    loadFailureBehavior: 'empty'
                },
                quantity: line.cartLine.Quantity ?? 0
            }));

        const numberOfCartLines = this._countItems(cartlines);
        const itemText = numberOfCartLines > 1 ? itemsText : singleItemText;
        const itemTitle = `(${format(itemText, numberOfCartLines)})`;

        const lineImageProps: ICheckoutShippingCartLinesProps = {
            moduleClassName: 'ms-checkout-shipping-address',
            cartLines: cartlines.filter(x => !ObjectExtensions.isNullOrUndefined(x)),
            itemTitle: itemTitle,
            title: headingImages,
            resources: this.props.resources
        };

        const cartLineImages: React.ReactNode = <CheckoutPickupCartLines {...lineImageProps} />;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { featureState } = this.props.data;
        const isRetailMultiplePickUpOptionEnabled = featureState.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;

        const errorMessage = this.state.errorMessage;

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            addressListSelectedAddress: this.addressListSelectedAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            hasEmailError: this.hasEmailError,
            hasStreetError: this.hasStreetError,
            hasCityError: this.hasCityError,
            hasPostalError: this.hasPostalError,
            hasStateError: this.hasStateError,
            emailErrorMessage: this.emailErrorMessage,
            streetErrorMessage: this.streetErrorMessage,
            cityErrorMessage: this.cityErrorMessage,
            postalErrorMessage: this.postalErrorMessage,
            stateErrorMessage: this.stateErrorMessage,

            className: config.className,
            viewState: {
                isShowAddress: this.currentOperation === AddressOperation.Show && this.selectedAddress,
                isShowAddresList: this.currentOperation === AddressOperation.List && this.customerAddresses.length > 0,
                isShowAddOrUpdateAddress:
                    this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update
            },
            CheckoutShippingAddress: {
                moduleProps: this.props,
                className: classnames('ms-checkout-shipping-address', config.className)
            },
            showAddress: this._renderShowAddress(),
            alert: hasError && errorMessage && <ErrorComponent {...{ title: errorMessageTitle, message: errorMessage }} />,
            checkoutErrorRef: this.checkoutErrorRef,

            /**
             * Show address select.
             * @param onAddAddressHandler - On add address click function.
             * @param onSaveHandler - On save click function.
             * @param onCancelHandler - On cancel click function.
             * @returns - Renders select address.
             */
            showAddressSelectHandler: (onAddAddressHandler?: () => void, onSaveHandler?: () => void, onCancelHandler?: () => void) =>
                this._renderSelectAddress(onAddAddressHandler, onSaveHandler, onCancelHandler),

            /**
             * Show add/update address.
             * @param onSaveHandler - On save click function.
             * @param onCancelHandler - On cancel click function.
             * @returns - Renders select address.
             */
            showAddOrUpdateAddressHandler: (onSaveHandler?: () => void, onCancelHandler?: () => void) =>
                this._renderAddOrUpdateAddress(onSaveHandler, onCancelHandler),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
            cartLineImages: isRetailMultiplePickUpOptionEnabled ? cartLineImages : undefined,
            showAddressSelect: this._renderSelectAddress(),
            companyName: this._renderCompanyName(),
            deliveryNotes: this._renderDeliveryNotes(),
            email: this._renderEmail(),
            isCheckboxChecked: this._renderCheckbox()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private _renderEmail = (): JSX.Element => {
        const { email } = this.state;
        let _emailValue = '';

        if (this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines) {
            const extensionProperties = this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines[0].ShippingAddress
                ?.ExtensionProperties;
            if (email === '') {
                const email = extensionProperties?.find(prop => prop.Key === 'EMAILADDRESS')?.Value?.StringValue || '';
                _emailValue = email;
            } else {
                _emailValue = email;
            }
        }
        return (
            <div className={'email address-form__item-invalid'}>
                {(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) && (
                    <>
                        <label className='msc-address-form__label'>{this.props.resources.companyEmailText} *</label>
                        {this.hasEmailError && (
                            <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                {this.emailErrorMessage}
                            </span>
                        )}
                        <input
                            ref={this.emailRef}
                            id='email'
                            aria-required='true'
                            className='msc-address-form__input msc-address-form__input-text'
                            type='text'
                            value={_emailValue}
                            onChange={this._onEditEmail}
                        />
                    </>
                )}
                {this.currentOperation === AddressOperation.Show && <div className='checkout-company-companyName'>{email}</div>}
            </div>
        );
    };

    private readonly isEmailValid = (email: string): boolean => {
        return EmailRegex.defaultRegex.test(email);
    };

    private _renderCompanyName = (): JSX.Element => {
        const { companyName } = this.state;
        let _companyValue = '';
        if (this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines) {
            const extensionProperties = this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines[0].ShippingAddress
                ?.ExtensionProperties;
            if (companyName === '') {
                const companyName = extensionProperties?.find(prop => prop.Key === 'COMPANYNAME')?.Value?.StringValue || '';
                _companyValue = companyName;
            } else {
                _companyValue = companyName;
            }
        }
        return (
            <div className={'company-name'}>
                {(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) && (
                    <>
                        <label className='msc-address-form__label'>{this.props.resources.companyNameText}</label>
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive'></span>
                        <input
                            ref={this.companyNameRef}
                            id='companyName'
                            className='msc-address-form__input msc-address-form__input-text'
                            type='text'
                            value={_companyValue}
                            onChange={this._onEditCompanyName}
                        />
                    </>
                )}
                {this.currentOperation === AddressOperation.Show && <div className='checkout-company-companyName'>{companyName}</div>}
            </div>
        );
    };

    private _renderDeliveryNotes = (): JSX.Element => {
        const { deliveryNotes } = this.state;
        let _noteValue = '';
        if (this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines) {
            const extensionProperties = this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines[0].ShippingAddress
                ?.ExtensionProperties;
            if (deliveryNotes === '') {
                const deliveryNotes = extensionProperties?.find(prop => prop.Key === 'ADDITIONALINFO')?.Value?.StringValue || '';
                _noteValue = deliveryNotes;
            } else {
                _noteValue = deliveryNotes;
            }
        }
        return (
            <div className={'delivery-notes'}>
                {(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) && (
                    <>
                        <label className='msc-address-form__label'>Additional Information</label>
                        <span className='msc-address-form__alert' role='alert' aria-live='assertive'></span>
                        <textarea
                            ref={this.deliveryNotesRef}
                            id='deliveryNotes'
                            aria-label='Additional Information'
                            placeholder={this.props.resources.deliveryNotesPlaceHolderText}
                            className='deliveryNotes-textarea'
                            value={_noteValue}
                            onChange={this._onEditDeliveryNotes}
                        />
                    </>
                )}
                {this.currentOperation === AddressOperation.Show && (
                    <div className='checkout-delivery-options__deliveryNotes'>{deliveryNotes}</div>
                )}
            </div>
        );
    };

    private _renderCheckbox = (): JSX.Element => {
        const { isCheckboxChecked, streetAddress, additionalCity, selectedState, selectedCountry, PostalCode } = this.state;
        let _checkValue = '';
        let _streetValue = '';
        let _cityValue = '';
        let _stateValue = '';
        let _countryValue = '';
        let _postalcodeValue = '';
        if (this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines) {
            const extensionProperties = this.props?.data?.checkout?.result?.checkoutCart?.cart?.CartLines[0].ShippingAddress
                ?.ExtensionProperties;

            if (isCheckboxChecked.toString() === '') {
                _checkValue = extensionProperties?.find(prop => prop.Key === 'SHIPPINGCHECK')?.Value?.StringValue || 'false';
                // Update state based on retrieved value
                this.setState({ isCheckboxChecked: _checkValue === 'true' });
            } else {
                _checkValue = isCheckboxChecked.toString();
            }

            if (streetAddress === '' && this.state.isFirstRender === true) {
                const streetAddress = extensionProperties?.find(prop => prop.Key === 'ADDITIONALSTREET')?.Value?.StringValue || '';
                _streetValue = streetAddress;
            } else {
                _streetValue = streetAddress;
            }

            if (additionalCity === '' && this.state.isFirstRender === true) {
                const City = extensionProperties?.find(prop => prop.Key === 'ADDITIONALCITY')?.Value?.StringValue || '';
                _cityValue = City;
            } else {
                _cityValue = additionalCity;
            }

            if (selectedState === '') {
                const stateFromExtension = extensionProperties?.find(prop => prop.Key === 'ADDITIONALSTATE')?.Value?.StringValue || '';
                _stateValue = stateFromExtension || selectedState; // Use value from ExtensionProperties if available
            } else {
                _stateValue = selectedState;
            }

            if (selectedCountry === '') {
                const countryFromExtension = extensionProperties?.find(prop => prop.Key === 'ADDITIONALCOUNTRY')?.Value?.StringValue || '';
                _countryValue = countryFromExtension || selectedCountry; // Use value from ExtensionProperties if available
            } else {
                _countryValue = selectedCountry;
            }

            if (PostalCode === '' && this.state.isFirstRender === true) {
                const PostalCode = extensionProperties?.find(prop => prop.Key === 'ADDITIONALZIPCODE')?.Value?.StringValue || '';
                _postalcodeValue = PostalCode;
            } else {
                _postalcodeValue = PostalCode;
            }
        }

        return (
            <div className=''>
                {(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) && (
                    <>
                        <div className='cus_separateAddress_checkbox_wrapper'>
                            <input
                                id='termsCheckbox'
                                className='msc-address-form__input-checkbox'
                                type='checkbox'
                                checked={_checkValue === 'true'} // Convert string to boolean
                                onChange={this._onToggleCheckbox}
                            />
                            <label htmlFor='termsCheckbox' className='msc-address-form__label'>
                                {this.props.resources.separateShippingText}
                            </label>
                        </div>

                        {(isCheckboxChecked || _checkValue === 'true') && (
                            <div className='additional-fields address-form__item-invalid'>
                                <label htmlFor='streetAddress' className='msc-address-form__label'>
                                    {this.props.resources.streetText}
                                </label>
                                {this.hasStreetError && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        {this.streetErrorMessage}
                                    </span>
                                )}
                                <input
                                    id='streetAddress'
                                    className='msc-address-form__input msc-address-form__input-text'
                                    type='text'
                                    value={_streetValue}
                                    onChange={this._onEditstreetAddress}
                                />

                                <label htmlFor='City' className='msc-address-form__label'>
                                    {this.props.resources.cityText}
                                </label>
                                {this.hasCityError && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        {this.cityErrorMessage}
                                    </span>
                                )}
                                <input
                                    id='City'
                                    className='msc-address-form__input msc-address-form__input-text'
                                    type='text'
                                    value={_cityValue}
                                    onChange={this._onEditCity}
                                />

                                <label htmlFor='stateDropdown' className='msc-address-form__label'>
                                    State
                                </label>
                                {this.hasStateError && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        {this.stateErrorMessage}
                                    </span>
                                )}
                                <select
                                    id='stateDropdown'
                                    className='msc-address-form__select'
                                    value={_stateValue} // Use the preselected value if available
                                    onChange={this._onStateChange}
                                >
                                    <option value=''>{this.props.resources.stateText}</option>
                                    {(this.state.stateProvinceInfo || []).map((state, index) => (
                                        <option key={state.StateId || index} value={state.StateName || ''}>
                                            {state.StateName || 'Unknown'}
                                        </option>
                                    ))}
                                </select>

                                <label htmlFor='countryDropdown' className='msc-address-form__label'>
                                    {this.props.resources.countryText}
                                </label>
                                <select
                                    id='countryDropdown'
                                    className='msc-address-form__dropdown'
                                    value={_countryValue || this.countryRegionId} // Use the preselected value or default to 'USA'
                                    onChange={this._onCountrysChange}
                                >
                                    <option value=''>Select Country</option>
                                    {this.addressFormat.getCountryFormat()?.map((country, index) => (
                                        <option key={country.key || index} value={country.key || ''}>
                                            {country.value || 'Unknown'}
                                        </option>
                                    ))}
                                </select>

                                <label htmlFor='Postalcode' className='msc-address-form__label'>
                                    {this.props.resources.postalCodeText}
                                </label>
                                {this.hasPostalError && (
                                    <span className='msc-address-form__alert' role='alert' aria-live='assertive'>
                                        {this.postalErrorMessage}
                                    </span>
                                )}
                                <input
                                    id='Postalcode'
                                    className='msc-address-form__input msc-address-form__input-text'
                                    type='text'
                                    value={_postalcodeValue}
                                    onChange={this._onEditPostalCode}
                                />
                            </div>
                        )}
                    </>
                )}

                {this.currentOperation === AddressOperation.Show && (
                    <div className='checkout-checkbox-status'>
                        {isCheckboxChecked ? (
                            <>
                                <p>Street Address: {streetAddress}</p>
                                <p>City: {additionalCity}</p>
                                <p>Country: {selectedCountry}</p>
                                <p>State: {selectedState}</p>
                                <p>PostalCode: {PostalCode}</p>
                            </>
                        ) : (
                            ''
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Add the following methods for handling field inputs:
    private _onEditstreetAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.hasStreetError = false;
        const streetAddress = event.target.value;
        if (streetAddress === '') {
            this.hasStreetError = true;
            this.streetErrorMessage = 'Street is required';
        }
        this.setState({ streetAddress: event.target.value });
        this.setState({ isEditForm: true });
    };

    private _onEditCity = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.hasCityError = false;
        const City = event.target.value;
        if (City === '') {
            this.hasCityError = true;
            this.cityErrorMessage = 'City is required';
        }
        this.setState({ additionalCity: event.target.value });
        this.setState({ isEditForm: true });
    };

    private _onEditPostalCode = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.hasPostalError = false;
        const PostalCode = event.target.value;
        if (PostalCode === '') {
            this.hasPostalError = true;
            this.postalErrorMessage = 'Postal Code is required';
        }
        this.setState({ PostalCode: event.target.value });
        this.setState({ isEditForm: true });
    };

    private _onStateChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        // Update the selected state in the state
        this.hasStateError = false;
        const selectedState = event.target.value;
        if (selectedState === '') {
            this.hasStateError = true;
            this.stateErrorMessage = 'State is required';
        }
        this.setState({ selectedState: event.target.value });
        this.setState({ isEditForm: true });
    };

    private _onCountrysChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedCountry = event.target.value;
        this.setState({ selectedCountry }, () => {
            if (selectedCountry) {
                // Fetch and set the states for the selected country
                this.addressCommon.getStateProvinces(selectedCountry).then((result: StateProvinceInfo[]) => {
                    this.setState({
                        stateProvinceInfo: result,
                        selectedState: '' // Reset selectedState when country changes
                    });
                });
            } else {
                // Clear the states if no country is selected
                this.setState({ stateProvinceInfo: [], selectedState: '' });
            }
        });
    };

    // Update the checkbox toggle handler:
    private _onToggleCheckbox = (): void => {
        const { isCheckboxChecked } = this.state;
        this.setState({ isCheckboxChecked: !isCheckboxChecked }, () => {});
    };

    private _getAttributeValue = (fieldName: string) => {
        const deliveryNotes =
            this.props.data.checkout.result &&
            this.props.data.checkout.result.checkoutCart &&
            this.props.data.checkout.result.checkoutCart.cart &&
            this.props.data.checkout.result.checkoutCart.cart?.AttributeValues?.find(attribute => attribute.Name === fieldName);

        if (deliveryNotes) {
            // @ts-expect-error -- Need to provide data type.

            return deliveryNotes?.TextValue;
        }

        return undefined;
    };

    private _onEditDeliveryNotes = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const textArea: HTMLTextAreaElement | null =
            this.deliveryNotesRef && this.deliveryNotesRef.current && this.deliveryNotesRef.current;

        const deliveryNotes = (this.deliveryNotesRef && this.deliveryNotesRef.current && this.deliveryNotesRef.current.value) || '';

        const text: string = deliveryNotes;

        textArea!.value = text;

        this.setState({ deliveryNotes: text });
    };

    private _onEditCompanyName = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const inputtext: HTMLInputElement | null = this.companyNameRef && this.companyNameRef.current && this.companyNameRef.current;

        const companyName = (this.companyNameRef && this.companyNameRef.current && this.companyNameRef.current.value) || '';

        const text: string = companyName;

        inputtext!.value = text;

        this.setState({ companyName: text });
    };

    private _onEditEmail = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.hasEmailError = false;
        const inputtext: HTMLInputElement | null = this.emailRef && this.emailRef.current && this.emailRef.current;

        const email = (this.emailRef && this.emailRef.current && this.emailRef.current.value) || '';
        if (email === '') {
            this.hasEmailError = true;
            this.emailErrorMessage = 'Email is required';
        }
        const isValidEmail = this.isEmailValid(email);
        if (!isValidEmail) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'Email is invalid';
        }

        const text: string = email;

        inputtext!.value = text;

        this.setState({ email: text });

        this.props.data.checkout.result?.updateGuestCheckoutEmail({ newGuestCheckoutEmail: text });
    };

    /**
     * Initialize pickup group.
     */
    @action
    private readonly _initPickupGroup = async (): Promise<void> => {
        const pickupCartLines: CartLine[] = this._getCartLinesforDelivery();
        const cartLines: ICartLineWithProduct[] = [];

        try {
            const products = await this._getProductsByCartLines(
                this.props.data.checkout.result?.checkoutCart.cart.ChannelId || 0,
                pickupCartLines
            );
            for (const line of pickupCartLines) {
                const product: SimpleProduct | undefined = products.find(x => x.RecordId === line.ProductId);
                cartLines.push({ cartLine: line, product });
            }
            this.setState({ shippingGroups: cartLines });
        } catch (error) {
            this.props.telemetry.error(error);
            this.setState({ shippingGroups: [] });
        }
    };

    /**
     * On suggestion selected.
     * @param result - Suggestion result interface.
     */
    @action
    private readonly _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        const timeout = 0;
        set(this.addUpdateAddress, { Street: '' });
        set(this.addUpdateAddress, { ZipCode: address.ZipCode });
        set(this.addUpdateAddress, { CountyName: address.CountyName });
        set(this.addUpdateAddress, { City: address.City });
        set(this.addUpdateAddress, { State: address.State });
        set(this.addUpdateAddress, { DistrictName: address.DistrictName });
        set(this.addUpdateAddress, { FullAddress: address.FullAddress });

        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { Street: address.Street });
        }, timeout);
    };

    /**
     * Method to clear address fields.
     */
    @action
    private readonly _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(
            this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        );
        for (const formatAddress of addressFormatItem) {
            if (
                this.addUpdateAddress[formatAddress.name] !== undefined &&
                !this.autoSuggest?.excludedAddressFields.includes(formatAddress.name)
            ) {
                this.addressFormat[formatAddress.name] = '';
            }
        }
        this._clearValidation();
    };

    /**
     * Method to clear validation.
     */
    @action
    private readonly _clearValidation = (): void => {
        this.validationError = {};
    };

    public get expressPaymentDetailsFromCartPage(): IExpressPaymentDetails | null {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const properties =
            this.props.data.cart?.result?.cart?.ExtensionProperties?.find(property => property.Key === 'expressPaymentDetails')?.Value
                ?.StringValue ?? '';

        return properties ? (JSON.parse(properties) as IExpressPaymentDetails) : null;
    }

    /**
     * Method data initialization.
     * @param props -The checkout address properties.
     */
    private readonly _dataInitialize = (props: ICheckoutAddressProps): void => {
        const { data } = props;

        reaction(
            () => data.countryRegions.result,
            () => {
                this.countryRegions = data.countryRegions.result ?? [];
            }
        );

        reaction(
            () => data.addressPurposes.result,
            () => {
                this.addressPurposes = data.addressPurposes.result ?? [];
            }
        );

        reaction(
            () => data.address.result,
            () => {
                this.customerAddresses = data.address.result ?? [];
            }
        );

        reaction(
            () => data.countryStates.result,
            () => {
                this.stateProvinceInfo = data.countryStates.result ?? [];
            }
        );

        reaction(
            () => data.featureState.result,
            () => {
                this.retailMultiplePickUpOptionEnabled = data.featureState.result?.find(
                    feature => feature.Name === this.multiplePickupStoreSwitchName
                )?.IsEnabled;
            }
        );
    };

    /**
     * Method to get cart lines for delivery.
     * @returns The cart line collection.
     */
    private readonly _getCartLinesforDelivery = (): CartLine[] => {
        return this.props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(line => this._isDelivery(line)) ?? [];
    };

    /**
     * Method to check cart line for delivery.
     * @param line -The cart line.
     * @returns True/false as per cart line delivery mode.
     */
    private readonly _isDelivery = (line: CartLine): boolean => {
        return this._isNotPickupMode(line.DeliveryMode) && (line.FulfillmentStoreId === undefined || line.FulfillmentStoreId === '');
    };

    /**
     * Method to check cart line for delivery.
     * @param deliveryMode -The delivery mode.
     * @returns True/false as per cart line delivery mode.
     */
    private readonly _isNotPickupMode = (deliveryMode: string | undefined): boolean => {
        const pickupDeliveryModeCode = this.props.context.request.channel?.PickupDeliveryModeCode;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const { channelDeliveryOptionConfig, featureState } = this.props.data;
        const retailMultiplePickUpOptionEnabled = featureState.result?.find(feature => feature.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;
        if (retailMultiplePickUpOptionEnabled && deliveryMode !== undefined) {
            const pickupDeliveryMode = channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.some(
                pickupMode => pickupMode !== deliveryMode
            );
            return pickupDeliveryMode !== undefined ? pickupDeliveryMode : false;
        }
        return deliveryMode !== undefined && pickupDeliveryModeCode !== undefined && deliveryMode !== pickupDeliveryModeCode;
    };

    /**
     * Method to check cart line for delivery.
     * @param channelId - The channelId.
     * @param cartLines - Cart line collections.
     * @returns Collection of SimpleProduct.
     */
    private readonly _getProductsByCartLines = async (channelId: number, cartLines: CartLine[]): Promise<SimpleProduct[]> => {
        const actionContext = this.props.context.actionContext;
        const productInputs = cartLines
            .filter(line => !ObjectExtensions.isNullOrUndefined(line.ProductId))
            .map(
                line =>
                    new ProductInput(
                        line.ProductId || 0,
                        actionContext.requestContext.apiSettings,
                        channelId,
                        undefined,
                        actionContext.requestContext,
                        line.CatalogId || 0
                    )
            );
        return getSimpleProducts(productInputs, actionContext);
    };

    /**
     * Method to render add/update address.
     * @param onSaveHandler -- Handles onsave functionality.
     * @param onCancelHandler -- Handles oncancel functionality.
     * @returns Address app/update props.
     */
    private readonly _renderAddOrUpdateAddress = (onSaveHandler?: () => void, onCancelHandler?: () => void): IAddressAddUpdateProps => {
        const addressFormat =
            this.currentOperation === AddressOperation.Add && this.addressCommon.isAuthenticatedFlow()
                ? this.addressFormat
                : this.addressFormat;

        /**
         * On Cancel Button Function.
         */
        const onCancelButtonHandler = () => {
            this._resetView();
            onCancelHandler?.();
        };
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            hasError: this.hasError,
            addressActionResponse: this.addressActionResponse,
            telemetryContent: this.telemetryContent,
            dropdownDisplayData: addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,

            /**
             * On Save Function.
             */
            onSave: () => {
                this.onSubmit();
                onSaveHandler?.();
            },

            onCancel: !this.selectedAddress && !this.addressCommon.isAuthenticatedFlow() ? () => {} : onCancelButtonHandler
        });
    };

    /**
     * Method to render select address.
     * @param onAddAddressHandler - To handle add address button click.
     * @param onSaveHandler - To handle save button click.
     * @param onCancelHandler - To handle cancel button click.
     * @returns Select address props.
     */
    private readonly _renderSelectAddress = (
        onAddAddressHandler?: () => void,
        onSaveHandler?: () => void,
        onCancelHandler?: () => void
    ): IAddressSelectProps => {
        /**
         * On Cancel Button Function.
         */
        const onCancelButtonHandler = () => {
            this._resetView();
            onCancelHandler?.();
        };
        return AddressSelect({
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            resources: this.resources,
            addressPurposes: this.addressPurposes,
            selectedAddress: this.addressListSelectedAddress,
            onAddressOptionChange: this._onAddressOptionChange,
            hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
            telemetryContent: this.telemetryContent,

            /**
             * On Add Address Function.
             */
            onAddAddress: () => {
                this._goToAddAddress();
                onAddAddressHandler?.();
            },

            /**
             * On Save Function.
             */
            onSave: () => {
                this._onSelectAddress();
                onSaveHandler?.();
            },

            onCancel: !this.selectedAddress ? () => {} : onCancelButtonHandler,

            multipleAddressShippingEnabled: this.props.config.multipleAddressShippingEnabled
        });
    };

    /**
     * Method to render show address.
     * @returns Show address props.
     */
    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({
                address: this.selectedAddress,
                addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || ''),
                addressPurposes: this.addressPurposes
            });
        }

        return null;
    }

    /**
     * Method to render map auto suggest.
     */
    private readonly _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    };

    /**
     * Method gets called on address option change.
     * @param event - To get current option.
     */
    private readonly _onAddressOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const addressRecordId = event.currentTarget.value;
        const selectedAddress = this.customerAddresses.find(address => (address.RecordId || '').toString() === addressRecordId);
        if (selectedAddress) {
            this.addressListSelectedAddress = selectedAddress;
        }
    };

    /**
     * Method to render map auto suggest.
     * @param name - Address name field.
     * @param value - Address name value field.
     */
    private readonly onAddressAddUpdate = (name: string, value: string | boolean) => {
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    };

    /**
     * Method to get called on address update change.
     * @param event - Input element.
     */
    private readonly _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    };

    /**
     * Method to get called on address update dropdown change.
     * @param event - Select element.
     */
    private readonly _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    /**
     * Method to get called on address update submit.
     */
    private readonly _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            return;
        }

        const email = this.emailRef.current?.value || '';
        const isValidEmail = this.isEmailValid(email);
        if (!email.trim()) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'Email is required';
            return;
        }
        if (!isValidEmail) {
            this.hasEmailError = true;
            this.emailErrorMessage = 'Email is invalid';
            return;
        }

        const streetAddress = this.state.streetAddress;
        const additionalCity = this.state.additionalCity;
        const postalCode = this.state.PostalCode;
        const selectedState = this.state.selectedState;

        if (this.state.isCheckboxChecked) {
            let hasError = false;
            if (streetAddress === '') {
                this.hasStreetError = true;
                this.streetErrorMessage = 'Street is required';
                hasError = true;
            }
            if (additionalCity === '') {
                this.hasCityError = true;
                this.cityErrorMessage = 'City is required';
                hasError = true;
            }
            if (postalCode === '') {
                this.hasPostalError = true;
                this.postalErrorMessage = 'Postal Code is required';
                hasError = true;
            }
            if (selectedState === '') {
                this.hasStateError = true;
                this.stateErrorMessage = 'State is required';
                hasError = true;
            }
            if (hasError === true) {
                return;
            }
        }
        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow() && this._isNewAddress(this.addUpdateAddress)) {
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this.isUpdating = true;
        this._updateModuleState();
        this.setState({
            isFirstRender: false
        });

        response.then(
            (result: IAddressResponse) => {
                this.isUpdating = false;

                if (result.address) {
                    this.hasError = false;
                    this._clearError();
                    if (result.customerAddresses) {
                        this._onAddOrUpdateSuccess({ customerAddresses: result.customerAddresses, address: result.address });
                    } else {
                        this._onAddOrUpdateSuccess({ address: result.address });
                    }
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                    this._updateModuleState();
                }
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
                this._updateModuleState();
            }
        );
    };

    /**
     * Method to get called on country change.
     * @param countryRegionId - Country region id.
     */
    private readonly _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterIsoRegionName = this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ThreeLetterISORegionName: countryRegionId });
        set(this.addUpdateAddress, { TwoLetterISORegionName: twoLetterIsoRegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterIsoRegionName);
        this._clearAddressFields();
    };

    /**
     * Method to get all address format exclude list.
     * @returns Collection of address items.
     */
    private readonly _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    };

    /**
     * Method to get all state/provinces.
     */
    private readonly _getStateProvinces = (): void => {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);

            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { State: '' });
            }

            this.stateProvinceInfo = result;
        });
    };

    /**
     * Method to set default country region id.
     */
    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request.channel?.ChannelCountryRegionISOCode;
        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    /**
     * Method to get default address region id.
     * @returns - Address object from existing addresses.
     */
    private readonly _getDefaultAddress = (): Address | undefined => {
        if (this.customerAddresses) {
            const primaryAddress = this.customerAddresses.find((address: Address) => address.IsPrimary);
            return primaryAddress || (this.customerAddresses.length > 0 ? this.customerAddresses[0] : undefined);
        }
        return undefined;
    };

    /**
     * Method to get address from express payment details from cart page.
     * @returns - Address.
     */
    private readonly _getAddressFromCartExpressPaymentDetails = (): Address | undefined => {
        if (this.expressPaymentDetailsFromCartPage) {
            const { tokenizedPaymentCard, address, isExpressAddressAppliedInCartPage } = this.expressPaymentDetailsFromCartPage;

            let addressFromCartExpress: Address | undefined;

            if (isExpressAddressAppliedInCartPage) {
                addressFromCartExpress = address ? this.getExpressAddress(address) : undefined;
            } else {
                addressFromCartExpress = tokenizedPaymentCard ? this.getAddressFromTokenizedPaymentCard(tokenizedPaymentCard) : undefined;
            }

            return addressFromCartExpress;
        }
        return undefined;
    };

    /**
     * Get address from tokenizedPaymentCard.
     * @param tokenizedPaymentCard -- The tokenizedPaymentCard from the payment.
     * @returns The address.
     */
    private readonly getAddressFromTokenizedPaymentCard = (tokenizedPaymentCard: TokenizedPaymentCard): Address => {
        const twoLetterIsoRegionName = tokenizedPaymentCard.Country;

        const threeLetterIsoRegionName = twoLetterIsoRegionName ? this.getThreeLetterIsoRegionName(twoLetterIsoRegionName) : undefined;

        if (threeLetterIsoRegionName && threeLetterIsoRegionName !== this.countryRegionId) {
            this._onCountryChange(threeLetterIsoRegionName);
        }

        const address: Address = {
            TwoLetterISORegionName: twoLetterIsoRegionName,
            Name: tokenizedPaymentCard.NameOnCard,
            Street: tokenizedPaymentCard.Address1,
            StreetNumber: tokenizedPaymentCard.Address2,
            City: tokenizedPaymentCard.City,
            State: tokenizedPaymentCard.State,
            ZipCode: tokenizedPaymentCard.Zip,
            Phone: tokenizedPaymentCard.Phone,
            ThreeLetterISORegionName: threeLetterIsoRegionName,
            AddressTypeValue: this.defaultAddressType
        };

        return address;
    };

    /**
     * Get address from expressAddress.
     * @param expressAddress -- The expressAddress.
     * @returns The address.
     */
    private readonly getExpressAddress = (expressAddress: Address): Address => {
        const twoLetterIsoRegionName = expressAddress.TwoLetterISORegionName;

        const threeLetterIsoRegionName = twoLetterIsoRegionName ? this.getThreeLetterIsoRegionName(twoLetterIsoRegionName) : undefined;

        if (threeLetterIsoRegionName && threeLetterIsoRegionName !== this.countryRegionId) {
            this._onCountryChange(threeLetterIsoRegionName);
        }

        const address: Address = {
            ...expressAddress,
            ThreeLetterISORegionName: threeLetterIsoRegionName,
            AddressTypeValue: this.defaultAddressType
        };

        return address;
    };

    /**
     * Get three letter ISO region name from two letter ISO region name.
     * @param twoLetterIsoRegionName -- The three letter ISO region name.
     * @returns The three letter ISO region name.
     */
    private readonly getThreeLetterIsoRegionName = (twoLetterIsoRegionName: string): string | undefined => {
        const countryRegion = this.countryRegions.find(country => {
            return country.ISOCode?.toLowerCase() === twoLetterIsoRegionName.toLowerCase();
        });

        return countryRegion?.CountryRegionId;
    };

    /**
     * Method to initialize all module state.
     */
    private readonly _initModuleState = (): void => {
        this.props.moduleState.init({
            status: this._canShip() ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        const checkoutState = this.props.data.checkout.result;

        if (this._canShip()) {
            let defaultAddress;
            const shippingCartLines: CartLine[] = this._getCartLinesforDelivery();

            if (checkoutState?.checkoutCart.cart.ShippingAddress) {
                defaultAddress = checkoutState.checkoutCart.cart.ShippingAddress;
            } else if (
                ArrayExtensions.hasElements(shippingCartLines) &&
                shippingCartLines[0].ShippingAddress !== undefined &&
                shippingCartLines[0].ShippingAddress.FullAddress !== ''
            ) {
                defaultAddress = shippingCartLines[0].ShippingAddress;
            } else if (this._getAddressFromCartExpressPaymentDetails()) {
                defaultAddress = this._getAddressFromCartExpressPaymentDetails();
            } else if (this._getDefaultAddress()) {
                defaultAddress = this._getDefaultAddress();
            } else {
                defaultAddress = checkoutState?.isExpressCheckoutApplied ? checkoutState.shippingAddressFromExpressCheckout : undefined;
            }

            if (defaultAddress && !this.addressCommon.isEmpty(defaultAddress)) {
                this._updateCurrentOperation(AddressOperation.Show, defaultAddress);
                this._setShippingAddress(defaultAddress);
                this._updateModuleState();
                checkoutState?.updateHasShippingAddress({ newHasShippingAddress: true });
            } else {
                this._updateCurrentOperation(AddressOperation.Add);
                checkoutState?.updateHasShippingAddress({ newHasShippingAddress: false });
            }
        }
    };

    /**
     * Method get called on submit address.
     */
    private readonly onSubmit = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this.props.telemetry.information('Shipping address submit for Add or Update operation.');
                this._onAddressAddUpdateSubmit();
                break;
            case AddressOperation.List:
                this.props.telemetry.information('Shipping address submit for List operation.');
                this._onSelectAddress();
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    /**
     * Method get called on cancel.
     */
    private readonly onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this.props.telemetry.information('Shipping address cancel for Add or Update operation.');
                this._clearAddressFields();
                if (!(!this.selectedAddress && !this.addressCommon.isAuthenticatedFlow())) {
                    this.setState({
                        deliveryNotes: this._getAttributeValue(deliveryNoteKey) || ''
                    });

                    this.setState({
                        email: this._getAttributeValue(emailKey) || ''
                    });

                    this.setState({
                        companyName: this._getAttributeValue(companyNameKey) || ''
                    });

                    this._resetView();
                }

                break;
            case AddressOperation.List:
                this.props.telemetry.information('Shipping address cancel for List operation.');
                if (this.selectedAddress) {
                    this.setState({
                        deliveryNotes: this._getAttributeValue(deliveryNoteKey) || ''
                    });

                    this.setState({
                        email: this._getAttributeValue(emailKey) || ''
                    });

                    this.setState({
                        companyName: this._getAttributeValue(companyNameKey) || ''
                    });

                    this._resetView();
                }
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    /**
     * Method get called on edit address.
     */
    private readonly onEdit = (): void => {
        this.props.telemetry.information('Shipping address onEdit is called.');

        if (this.addressCommon.isAuthenticatedFlow() && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.List, this.shippingAddress);
        } else if (this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Update, this.shippingAddress);
        }

        this._updateModuleState();
    };

    /**
     * Method to check if checkout cartlines are available fro shipping.
     * @returns - True/false as per the delivery mode.
     */
    private readonly _canShip = (): boolean => {
        const { checkout, channelDeliveryOptionConfig } = this.props.data;
        const { request } = this.props.context;
        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        const emailDeliveryModeCode = request && request.channel && request.channel.EmailDeliveryModeCode;
        if (!checkout.result || !request.channel || checkout.result.checkoutCart.isEmpty || checkout.result.checkoutCart.hasInvoiceLine) {
            return false;
        }

        // @ts-expect-error: Type-checker not realizing above request.channel check
        return this.retailMultiplePickUpOptionEnabled
            ? checkout.result.checkoutCart.cart.CartLines?.some(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !==
                            channelDeliveryOptionConfig.result?.PickupDeliveryModeCodes?.find(
                                deliveryMode => deliveryMode === cartLine.DeliveryMode
                            ) && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              )
            : checkout.result.checkoutCart.cart.CartLines?.some(cartLine =>
                  cartLine.DeliveryMode && cartLine.DeliveryMode !== ''
                      ? cartLine.DeliveryMode !== pickupDeliveryModeCode && cartLine.DeliveryMode !== emailDeliveryModeCode
                      : cartLine
              );
    };

    /**
     * Method get called on select address.
     */
    private readonly _onSelectAddress = () => {
        this._updateCurrentOperation(AddressOperation.Show, this.addressListSelectedAddress);
        this._setShippingAddress(this.addressListSelectedAddress);
        this._updateModuleState();
    };

    /**
     * Method get called on goto add address.
     */
    private readonly _goToAddAddress = () => {
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    /**
     * Method get called on add/update success.
     * @param response - Retail api response.
     */
    private readonly _onAddOrUpdateSuccess = (response: IAddressResponse) => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }

        if (response.address) {
            this._updateCurrentOperation(AddressOperation.Show, response.address);
            this._setShippingAddress(response.address);
            this._updateModuleState();
        }
    };

    /**
     * Method get called on set Shipping Address.
     * @param address - Retail Api address result.
     */
    private readonly _setShippingAddress = (address: Address): void => {
        if (this.props.data.checkout.result) {
            const newShippingAddress = { ...address };

            if (address.ThreeLetterISORegionName && !newShippingAddress.TwoLetterISORegionName) {
                newShippingAddress.TwoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(address.ThreeLetterISORegionName);
            }
            const _delivery = this.state.deliveryNotes;
            const _email = this.state.email;
            const _companyname = this.state.companyName;
            const _isCheckboxChecked = this.state.isCheckboxChecked ? 'true' : 'false';
            const _streetAddress = this.state.streetAddress;
            const _City = this.state.additionalCity;
            const _selectedCountry = this.state.selectedCountry || 'USA';
            const _selectedState = this.state.selectedState;
            const _postalCode = this.state.PostalCode;

            const deliveryexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALINFO');
            const emailexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'EMAILADDRESS');
            const companyexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'COMPANYNAME');
            const isCheckboxCheckedexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'SHIPPINGCHECK');
            const streetAddressexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALSTREET');

            const CityexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALCITY');
            const selectedCountryexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALCOUNTRY');
            const selectedStateexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALSTATE');
            const PostalCodeexistingProperty = newShippingAddress.ExtensionProperties?.find(prop => prop.Key === 'ADDITIONALZIPCODE');

            if (deliveryexistingProperty) {
                deliveryexistingProperty.Value = { StringValue: _delivery };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALINFO', Value: { StringValue: _delivery } });
            }

            if (emailexistingProperty) {
                emailexistingProperty.Value = { StringValue: _email };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'EMAILADDRESS', Value: { StringValue: _email } });
            }

            if (companyexistingProperty) {
                companyexistingProperty.Value = { StringValue: _companyname };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'COMPANYNAME', Value: { StringValue: _companyname } });
            }

            if (isCheckboxCheckedexistingProperty) {
                isCheckboxCheckedexistingProperty.Value = { StringValue: _isCheckboxChecked };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'SHIPPINGCHECK', Value: { StringValue: _isCheckboxChecked } });
            }

            if (streetAddressexistingProperty) {
                streetAddressexistingProperty.Value = { StringValue: _streetAddress };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALSTREET', Value: { StringValue: _streetAddress } });
            }

            if (CityexistingProperty) {
                CityexistingProperty.Value = { StringValue: _City };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALCITY', Value: { StringValue: _City } });
            }

            if (selectedCountryexistingProperty) {
                selectedCountryexistingProperty.Value = { StringValue: _selectedCountry };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALCOUNTRY', Value: { StringValue: _selectedCountry } });
            }

            if (selectedStateexistingProperty) {
                selectedStateexistingProperty.Value = { StringValue: _selectedState };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALSTATE', Value: { StringValue: _selectedState } });
            }

            if (PostalCodeexistingProperty) {
                PostalCodeexistingProperty.Value = { StringValue: _postalCode };
            } else {
                newShippingAddress.ExtensionProperties = newShippingAddress.ExtensionProperties || [];
                newShippingAddress.ExtensionProperties.push({ Key: 'ADDITIONALZIPCODE', Value: { StringValue: _postalCode } });
            }

            this.props.data.checkout.result.updateShippingAddress({ newShippingAddress });
            this.props.data.checkout.result.checkoutCart.updateShippingAddress({ newShippingAddress }).catch(error => {
                this.props.telemetry.error(error);
            });
        }
    };

    @computed private get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result?.shippingAddress;
    }

    /**
     * Method get called on reset view.
     */
    private readonly _resetView = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._updateCurrentOperation(
                    this.addressCommon.isAuthenticatedFlow() ? AddressOperation.List : AddressOperation.Show,
                    this.shippingAddress
                );
                break;
            default:
                this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        }
        this._updateModuleState();
    };

    /**
     * Update current operation.
     * @param operation - The address operation.
     * @param selectedAddress - The selected address.
     */
    private readonly _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = { ...this.selectedAddress } || {};
            set(this.addUpdateAddress, {
                ThreeLetterISORegionName: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
            });
            set(this.addUpdateAddress, {
                AddressTypeValue: this.addUpdateAddress.AddressTypeValue || this.defaultAddressType
            });
        } else if (this.currentOperation === AddressOperation.List) {
            this.addressListSelectedAddress = { ...this.selectedAddress } || {};
        }
    };

    /**
     * Update module state.
     */
    private readonly _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
        } else if (this.isUpdating) {
            this.props.moduleState.onPending();
        } else {
            this.props.moduleState.onUpdating();
        }
    };

    /**
     * Method to check if the customer already has the address.
     * @param address - The address.
     */
    private readonly _isNewAddress = (address: Address): boolean => {
        for (const existingAddress of this.customerAddresses) {
            if (
                (address.Name ?? '') === existingAddress.Name &&
                (address.Street ?? '') === existingAddress.Street &&
                (address.StreetNumber ?? '') === existingAddress.StreetNumber &&
                (address.City ?? '') === existingAddress.City &&
                (address.State ?? '') === existingAddress.State &&
                (address.ZipCode ?? '') === existingAddress.ZipCode &&
                (address.Phone ?? '') === existingAddress.Phone &&
                (address.ThreeLetterISORegionName ?? '') === existingAddress.ThreeLetterISORegionName &&
                (address.AddressTypeValue ?? '') === existingAddress.AddressTypeValue
            ) {
                return false;
            }
        }

        return true;
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            errorMessage
        });
    };

    private readonly _clearError = (): void => {
        this.props.moduleState.setHasError(false);
        this.setState({
            errorMessage: ''
        });
    };

    /**
     * Returns count of products.
     * @param cartLine - The checkout shipping cart line info props.
     * @returns Number of products.
     */
    private readonly _countItems = (cartlines: ICheckoutShippingCartLineInfo[]): number => {
        return cartlines.reduce((count, line) => {
            return count + (line.quantity || 0);
        }, 0);
    };
}

export default CheckoutShippingAddress;
